import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { FontLoader } from 'three/addons/loaders/FontLoader.js';
import { gsap } from "gsap";
import GoldSweep from '../particles/GoldSweep';   // ← replaces old Particles import
import PerfumeBottle from '../threeMeshes/bottlemesh';

export default class ThreeController {

    constructor() {
        this.clock        = new THREE.Clock();
        this.threeElements = [];
        this.init();
    }

    init() {
        this._load();
        this._setupScene();
        this.perfumeBottle = new PerfumeBottle(this.scene);
    }

    // ── Particles (GoldSweep) ────────────────────────────────────────────
    createParticles() {
        // GoldSweep creates its own canvas overlay — nothing added to the scene
        this.particles = new GoldSweep();
        // models is [] so the forEach in script.js is a safe no-op
        // Don't push to threeElements — GoldSweep.tick() is a no-op and
        // manages its own RAF loop internally
    }

    // Called by script.js navigateToSection — same interface as before
    smokeAnimation() {
        if (this.particles) {
            this.particles.smokeAnimation();
        }
    }

    // ── Bottle ───────────────────────────────────────────────────────────
    async addPerfumeBottle(perfumeImage, perfumeName) {
        await document.fonts.load('10pt "Tangerine"');
        if (!this.perfumeBottle) {
            this.perfumeBottle = new PerfumeBottle(this.scene);
        }
        this.perfumeBottle.addBottle(perfumeImage, perfumeName);
    }

    removeCurrentPerfumeBottle() {
        if (this.perfumeBottle) {
            this.perfumeBottle.removeBottle();
        }
    }

    // ── Loading manager (kept for Three.js API compatibility) ──────────────
    _load() {
        // LoadingManager stub — GLB loads lazily in bottlemesh.js on first
        // perfume click, so it doesn't block the loading screen.
        this.loadingManager = new THREE.LoadingManager();
    }

    // ── Real asset tracking — images + fonts ─────────────────────────────
    // Progress is honest: reflects actual download/parse state.
    // On slow mobile data this shows real progress, not a fake animation.
    _trackPageAssets() {
        const self = this;

        // ── IMAGES ───────────────────────────────────────────────────────
        const allImages = Array.from(document.images);
        const imgTotal  = allImages.length;
        let   imgLoaded = allImages.filter(img => img.complete && img.naturalWidth > 0).length;

        // ── FONTS ────────────────────────────────────────────────────────
        // document.fonts.ready resolves when all @font-face fonts are parsed.
        // On slow connections this can take 1-2 seconds — this is REAL wait time.
        let fontsReady = false;
        document.fonts.ready.then(function() { fontsReady = true; checkDone(); });

        // ── COMBINE: weight images at 80%, fonts at 20% ──────────────────
        // This feels honest: most of the wait is images, fonts are secondary.
        function calcPct() {
            const imgPct   = imgTotal > 0 ? (imgLoaded / imgTotal) * 80 : 80;
            const fontPct  = fontsReady ? 20 : 0;
            return Math.min(99, Math.round(imgPct + fontPct));
            // Never show 100% here — _completeLoadingScreen() does that
            // after both are truly done.
        }

        function updateBar(pct) {
            const bar    = document.getElementById('loading-bar');
            const pctEl  = document.getElementById('loading-percentage');
            if (bar)   bar.style.width = pct + '%';
            if (pctEl) pctEl.textContent = pct + '%';
        }

        function checkDone() {
            const allImgsDone = imgLoaded >= imgTotal || imgTotal === 0;
            if (allImgsDone && fontsReady) {
                updateBar(99);
                // Small breath before completing so user sees it reach the end
                setTimeout(function() { self._completeLoadingScreen(); }, 350);
            }
        }

        // Graceful: if nothing to track, complete after fonts
        if (imgTotal === 0) {
            updateBar(80);
            // fonts.ready handles the rest via checkDone()
            setTimeout(function() {
                if (!fontsReady) self._completeLoadingScreen();
            }, 10000); // safety net
            return;
        }

        // Images already all cached (e.g. repeat visit) — just wait for fonts
        if (imgLoaded >= imgTotal) {
            updateBar(80);
            // checkDone fires when fonts.ready resolves
            setTimeout(function() {
                if (!fontsReady) self._completeLoadingScreen();
            }, 10000);
            return;
        }

        // Track images in flight
        allImages.forEach(function(img) {
            if (!img.complete || img.naturalWidth === 0) {
                img.addEventListener('load', function() {
                    imgLoaded++;
                    updateBar(calcPct());
                    checkDone();
                }, { once: true });
                img.addEventListener('error', function() {
                    // Broken image still counts — never get stuck
                    imgLoaded++;
                    updateBar(calcPct());
                    checkDone();
                }, { once: true });
            }
        });

        // Draw initial real progress (some images may already be cached)
        updateBar(calcPct());

        // Safety net — max 12 seconds on island mobile data 😄
        setTimeout(function() { self._completeLoadingScreen(); }, 12000);
    }
    
    _completeLoadingScreen() {
        const screen = document.getElementById('loading-screen');
        const bar    = document.getElementById('loading-bar');
        if (!screen || screen.style.display === 'none') return;

        // Fill bar to 100% first
        if (bar) bar.style.width = '100%';

        // Brief pause so user sees 100%, then transparent fade
        setTimeout(() => {
            // ── KEY: disable pointer-events IMMEDIATELY so impatient
            // users can click nav/buttons the moment fade begins ──────
            screen.style.pointerEvents = 'none';

            // Fade out the loading screen opacity to 0 (transparent)
            // Site content underneath reveals naturally — no white flash
            gsap.to(screen, {
                opacity: 0,
                duration: 0.85,
                ease: 'power2.inOut',
                onComplete: () => {
                    screen.style.display = 'none';
                }
            });
        }, 320);
    }

    // ── Called from _trackPageAssets progress updates ─────────────────────
    _updateLoadingBar(pct) {
        const bar  = document.getElementById('loading-bar');
        const pctEl = document.getElementById('loading-percentage');
        if (bar)   bar.style.width = pct + '%';
        if (pctEl) pctEl.textContent = Math.round(pct) + '%';
    }
    // ── Scene setup ──────────────────────────────────────────────────────
    _setupScene() {
        const sizes = { width: window.innerWidth, height: window.innerHeight };

        this.canvas       = document.querySelector('canvas.webgl');
        this.scene        = new THREE.Scene();
        this.textureLoader = new THREE.TextureLoader();
        this.fontLoader   = new FontLoader();
        this.textureLoader.crossOrigin = '';

        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
        this.camera   = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100);
        this.scene.add(this.camera);

        this._configureCamera();
        this._configureRenderer(sizes);
        this._setControls();
        this._setupLights();
        this.tick();

        
        // ✅ new
        this._trackPageAssets();
    }

    _setupLights() {
        const dirLight = new THREE.DirectionalLight('#ffffff', 3);
        dirLight.castShadow = true;
        dirLight.shadow.camera.far = 15;
        dirLight.shadow.mapSize.set(1024, 1024);
        dirLight.shadow.normalBias = 0.05;
        dirLight.position.set(0.25, 3, -2.25);
        this.scene.add(dirLight);

        const pointLight = new THREE.PointLight('#f5c42b', 3);
        pointLight.castShadow = true;
        pointLight.shadow.camera.far = 15;
        pointLight.shadow.mapSize.set(1024, 1024);
        pointLight.shadow.normalBias = 0.05;
        pointLight.position.set(0.25, 3, -2.25);
        this.scene.add(pointLight);
    }

    _setControls() {
        const controls = new OrbitControls(this.camera, this.canvas);
        controls.enableDamping = true;
    }

    _configureRenderer(sizes) {
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.toneMapping         = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 0.55;
        this.renderer.shadowMap.enabled   = true;
        this.renderer.shadowMap.type      = THREE.PCFSoftShadowMap;
        this.renderer.setSize(sizes.width, sizes.height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }

    _configureCamera() {
        this.camera.position.set(0, 2, 5);
        this.camera.near = 0.1;
        this.camera.far  = 100;
        this.camera.updateProjectionMatrix();
    }

    // ── Tick ─────────────────────────────────────────────────────────────
    tickElements(time) {
        this.threeElements.forEach(el => el.tick(time));
    }

    tick() {
        const time = Date.now() * 0.0005;
        this.tickElements(time);

        // Bottle rotation is handled inside PerfumeBottle.tick()
        if (this.perfumeBottle) this.perfumeBottle.tick(time);

        this.renderer.render(this.scene, this.camera);
        requestAnimationFrame(this.tick.bind(this));
    }
}